"use client"
import { ArrowLeft, CalendarDays, Leaf, Pencil, Sprout } from "lucide-react";
import React from "react";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Separator } from "../../components/ui/separator";
import { Skeleton } from "../../components/ui/skeleton";
import { StatusBadge } from "../../components/ui/data-table/status-badge";
import { useChampignon } from "../../hooks/api/champignons/index";
import { useLogger } from "../../lib/logger/index";
import { imageService } from "../../lib/services";
import { formatDate } from "../../lib/utils";
import {
  getChampignonCategoryBadgeStyle,
  getChampignonCategoryLabel,
} from "./utils/champignon-category";

interface ChampignonsDetailProps {
  champignonsId: number;
  onBack: () => void;
  onEdit: () => void;
}

// ─── Reusable field ────────────────────────────────────────────────────────────
function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="text-sm font-medium text-foreground">{value ?? "—"}</dd>
    </div>
  );
}

// ─── Section heading ───────────────────────────────────────────────────────────
function SectionHeading({
  icon: Icon,
  children,
}: {
  icon?: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <h3 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
      {Icon && <Icon className="h-3.5 w-3.5 text-primary shrink-0" />}
      {children}
    </h3>
  );
}

// ─── Loading skeleton ──────────────────────────────────────────────────────────
function DetailSkeleton() {
  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4 pb-1">
        <div className="space-y-2">
          <Skeleton className="h-6 w-52" />
          <div className="flex gap-2">
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-16 rounded-full" />
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <Skeleton className="h-9 w-24 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>
      <Separator />
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="space-y-1.5">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        ))}
      </div>
      <Separator />
      <Skeleton className="h-52 w-full rounded-lg" />
    </div>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────
export function ChampignonsDetail({
  champignonsId,
  onBack,
  onEdit,
}: ChampignonsDetailProps) {
  const { data: champignon, isLoading, error } = useChampignon(champignonsId);
  const logger = useLogger("ChampignonsDetail");
  logger.debug("Fetching champignon details", { champignonsId });

  if (isLoading) return <DetailSkeleton />;

  if (error || !champignon?.data) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {error?.message ||
            "Impossible de charger les détails du champignon. Veuillez réessayer."}
        </AlertDescription>
      </Alert>
    );
  }

  const data = champignon.data;
  const imgUrl = imageService.getPrimaryImage(data.img_url, "champignon");
  const categoryLabel = getChampignonCategoryLabel(data.category);
  const categoryBadgeStyle = getChampignonCategoryBadgeStyle(data.category);

  return (
    <div className="max-w-3xl mx-auto">
      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 pb-5">
        {/* Name + badges */}
        <div className="space-y-2 min-w-0">
          <h2 className="text-xl font-semibold tracking-tight text-foreground truncate">
            {data.name}
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            {categoryLabel && categoryBadgeStyle && (
              <Badge className={categoryBadgeStyle.className}>{categoryLabel}</Badge>
            )}
            <StatusBadge
              tone={data.is_deleted ? "deleted" : "active"}
              label={data.is_deleted ? "Supprimé" : "Actif"}
              withIcon
            />
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" onClick={onBack}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Retour
          </Button>
          {!data.is_deleted && (
            <Button onClick={onEdit}>
              <Pencil className="mr-1.5 h-4 w-4" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      <Separator />

      <div className="py-6 space-y-6">
        {/* ── Informations générales ── */}
        <div className="space-y-4">
          <SectionHeading icon={Leaf}>Informations générales</SectionHeading>
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
            <Field label="Identifiant" value={`#${data.id}`} />
            <Field label="Nom" value={data.name} />
            <Field label="Catégorie" value={categoryLabel ?? "—"} />
          </dl>
        </div>

        <Separator />

        {/* ── Propriétés d'attaque ── */}
        <div className="space-y-4">
          <SectionHeading icon={Sprout}>Propriétés d'attaque</SectionHeading>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field
              label="Attaque Feuillu"
              value={data.attaque_f || "Non spécifiée"}
            />
            <Field
              label="Attaque Résineux"
              value={data.attaque_r || "Non spécifiée"}
            />
          </dl>
        </div>

        <Separator />

        {/* ── Dates ── */}
        <div className="space-y-4">
          <SectionHeading icon={CalendarDays}>Dates</SectionHeading>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field
              label="Créé le"
              value={
                data.created_at
                  ? formatDate(new Date(data.created_at))
                  : "—"
              }
            />
            <Field
              label="Mis à jour le"
              value={
                data.updated_at
                  ? formatDate(new Date(data.updated_at))
                  : "—"
              }
            />
          </dl>
        </div>

        {/* ── Illustration ── */}
        {imgUrl && (
          <>
            <Separator />
            <div className="space-y-3">
              <SectionHeading>Illustration</SectionHeading>
              <div className="rounded-lg overflow-hidden border border-border bg-muted/30">
                <img
                  src={imageService.getOptimizedImageUrl(
                    data.img_url?.img1 || "",
                    "champignon",
                    800,
                    600
                  )}
                  alt={data.name as string}
                  className="w-full h-auto max-h-80 object-contain p-4"
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
